import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { api, money } from "@/lib/api";
import DocumentDialog from "@/components/DocumentDialog";
import ImportDialog from "@/components/ImportDialog";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, DotsThreeVertical, PaperPlaneTilt, CheckCircle, ArrowsClockwise, PencilSimple, DownloadSimple, FileArrowDown, UploadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const QUOTE_STATUSES = ["draft", "sent", "approved", "converted", "rejected"];

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = () => api.listQuotes().then(setQuotes).catch(() => {});
  useEffect(() => { load(); }, []);

  const shown = quotes.filter((q) =>
    filter === "all" ? true : filter === "unapproved" ? q.status !== "approved" && q.status !== "converted" : q.status === filter
  );

  const send = async (q) => {
    try {
      const res = await api.sendQuote(q.id);
      if (res.email?.status === "success") toast.success(`Quote emailed to ${q.client_email}`);
      else if (res.email?.status === "skipped") toast.warning("Quote marked sent (email not configured yet)");
      else toast.warning("Quote marked sent (email failed)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
  };
  const approve = async (q) => { await api.setQuoteStatus(q.id, "approved"); toast.success("Quote approved"); load(); };
  const convert = async (q) => { const job = await api.convertQuote(q.id); toast.success(`Converted to job ${job.number}`); load(); };
  const remove = async (q) => {
    if (!window.confirm(`Delete quote ${q.number}? This cannot be undone.`)) return;
    try { await api.deleteQuote(q.id); toast.success("Quote deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <PageHeader title="Quotes" subtitle="Estimates for your customers" actions={
        <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="quote-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All quotes</SelectItem>
              <SelectItem value="unapproved">Not approved</SelectItem>
              {QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => api.downloadCsv("quotes").catch(() => toast.error("Export failed"))} data-testid="export-quotes-csv"><DownloadSimple size={17} className="mr-1.5" /> Export CSV</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-quotes-button"><UploadSimple size={17} className="mr-1.5" /> Import CSV</Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#0B1726] hover:bg-slate-800" data-testid="add-quote-button"><Plus size={18} className="mr-1" /> New Quote</Button>
        </div>
      } />

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Quote #</TableHead><TableHead>Client</TableHead><TableHead>Status</TableHead>
            <TableHead className="text-right">Total</TableHead><TableHead className="w-10"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">No quotes{filter !== "all" ? " for this filter" : ""} yet.</TableCell></TableRow>}
            {shown.map((q) => (
              <TableRow key={q.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`quote-row-${q.id}`} onClick={() => navigate(`/quotes/${q.id}`)}>
                <TableCell className="font-mono font-semibold text-[#0B1726]">{q.number}</TableCell>
                <TableCell>{q.client_name}</TableCell>
                <TableCell><StatusBadge status={q.status} /></TableCell>
                <TableCell className="text-right font-semibold">{money(q.total)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`quote-menu-${q.id}`}><DotsThreeVertical size={20} weight="bold" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/quotes/${q.id}`)}><PencilSimple size={16} className="mr-2" /> Open</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => send(q)} data-testid={`quote-send-${q.id}`}><PaperPlaneTilt size={16} className="mr-2" /> Send to client</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => window.open(api.quotePdfUrl(q.id), "_blank")} data-testid={`quote-pdf-${q.id}`}><FileArrowDown size={16} className="mr-2" /> Download PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => approve(q)}><CheckCircle size={16} className="mr-2" /> Mark approved</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => convert(q)} disabled={q.status === "converted"} data-testid={`quote-convert-${q.id}`}><ArrowsClockwise size={16} className="mr-2" /> Convert to job</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => remove(q)} data-testid={`quote-delete-${q.id}`} className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"><Trash size={16} className="mr-2" /> Delete quote</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <DocumentDialog open={open} onClose={() => setOpen(false)} type="quote" initial={editing} onSaved={load} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} kind="quotes" onDone={load} />
    </div>
  );
}
